import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/redux/Store';
import { DashboardStats as DashboardStatsType, FavoriteStats, FollowStats } from '@/redux/slices/dashboardSlice';
import { selectFullAchievements } from '@/redux/selectors/achievementSelectors';
import HintTooltip from '@/components/HintTooltip';
import './DashboardStats.scss';

const AREA_RANK_EXPLAINER =
  "Ranked among everyone whose home or touring city matches yours, by highest profile price plus total royalties you've earned. The combined value is what places you on the area leaderboard.";

interface DashboardStatsProps {
  stats: DashboardStatsType | null;
  favoriteStats: FavoriteStats | null;
  followStats: FollowStats | null;
}

const DashboardStats: React.FC<DashboardStatsProps> = ({ stats, favoriteStats, followStats }) => {
  const navigate = useNavigate();

  // Get feed data from store
  const spotlightFeed = useAppSelector((state) => state.dashboard.spotlightFeed);
  const locationFeed = useAppSelector((state) => state.dashboard.locationFeed);
  const klyqueFeed = useAppSelector((state) => state.dashboard.klyqueFeed);

  // Achievements for the current user (fetched in DashboardPage on mount)
  const currentUsername = useAppSelector((s) => s.auth.user?.username);
  const achievements = useAppSelector((s) =>
    currentUsername ? selectFullAchievements(currentUsername)(s) : undefined
  );
  const recentAchievement = achievements?.earned[0];

  // Calculate total posts in feed
  const totalFeedPosts = spotlightFeed.length + locationFeed.length + klyqueFeed.length;

  const handleCardClick = (category: string) => {
    navigate(`/analytics?category=${category}`);
  };

  return (
    <section className="dashboard-stats-section">
      <div className="stats-grid">
        {/* Personal Stats */}
        <div className="stat-card clickable" onClick={() => handleCardClick('trades')}>
          <h3>Your Profiles</h3>
          <div className="stat-value">{stats?.personal.ownedProfiles || 0}</div>
          <div className="stat-label">Profiles Owned</div>
          <div className="stat-hint">Click for details</div>
        </div>

        {/* Klyque/Klicks */}
        <div className="stat-card clickable" onClick={() => handleCardClick('followers')}>
          <h3>Klyque</h3>
          <div className="stat-value">{followStats?.klyqueCount || 0}</div>
          <div className="stat-label">Following</div>
          <div className="stat-subtext">
            {followStats?.klicksCount || 0} Klicks (followers)
          </div>
          <div className="stat-hint">Click for details</div>
        </div>

        {/* Feed */}
        <div className="stat-card clickable feed-card" onClick={() => navigate('/feed')}>
          <h3>Your Feed</h3>
          <div className="stat-value">{totalFeedPosts}</div>
          <div className="stat-label">Posts available</div>
          <div className="stat-subtext">
            From {klyqueFeed.length > 0 ? 'your network' : 'trending'}
          </div>
          <div className="stat-hint">View feed</div>
        </div>

        {/* Klyx'd */}
        <div className="stat-card clickable" onClick={() => handleCardClick('engagement')}>
          <h3>Klyx'd Today</h3>
          <div className="stat-value">{favoriteStats?.received.today || 0}</div>
          <div className="stat-label">Times favorited</div>
          <div className="stat-subtext">
            {favoriteStats?.received.thisWeek || 0} this week
          </div>
          <div className="stat-hint">Click for details</div>
        </div>

        {/* Activity */}
        <div className="stat-card clickable" onClick={() => handleCardClick('trades')}>
          <h3>Activity (24h)</h3>
          <div className="stat-row">
            <div>
              <div className="stat-value small">{stats?.personal.buys24h || 0}</div>
              <div className="stat-label">Buys</div>
            </div>
            <div>
              <div className="stat-value small">{stats?.personal.sells24h || 0}</div>
              <div className="stat-label">Sells</div>
            </div>
          </div>
          <div className="stat-hint">Click for details</div>
        </div>

        {/* Royalties Earned — lifetime as the headline, 30d activity
            as an optional subtext line. Previously gated on
            royalties30d > 0, which hid the card entirely for creators
            whose last royalty was more than 30 days ago — even when
            lifetime earnings were meaningful (issue #528). */}
        {stats && stats.personal.totalRoyaltiesEarned > 0 && (
          <div className="stat-card highlight clickable" onClick={() => navigate('/analytics?category=royalties')}>
            <h3>Royalties</h3>
            <div className="stat-value">{stats.personal.totalRoyaltiesEarned.toFixed(2)}</div>
            <div className="stat-label">KLV earned lifetime</div>
            {stats.personal.royalties30d > 0 && (
              <div className="stat-subtext">
                +{stats.personal.royalties30d.toFixed(2)} KLV in last 30d
              </div>
            )}
            <div className="stat-hint">View analytics</div>
          </div>
        )}

        {/* Area Ranking — only shows when the user has a home location set */}
        {stats?.area.hasLocation && (
          <div className="stat-card clickable" onClick={() => navigate('/leaderboards')}>
            <h3>
              Area Rank <HintTooltip text={AREA_RANK_EXPLAINER} placement="bottom" />
            </h3>
            <div className="stat-value">#{stats.area.userRank}</div>
            <div className="stat-label">In your area</div>
            <div className="stat-subtext">
              Out of {stats.area.totalUsersInArea} users
            </div>
            <div className="stat-hint">View leaderboard</div>
          </div>
        )}

        {/* Achievements */}
        {achievements && (
          <div className="stat-card clickable" onClick={() => navigate('/achievements')}>
            <h3>🏆 Achievements</h3>
            <div className="stat-value">
              {achievements.stats.unlocked}
              <span style={{ fontSize: '0.5em', opacity: 0.6 }}> / {achievements.stats.total}</span>
            </div>
            <div className="stat-label">Unlocked</div>
            <div className="stat-subtext">
              {recentAchievement
                ? `Latest: ${recentAchievement.icon} ${recentAchievement.name}`
                : 'Earn your first to start the collection'}
            </div>
            <div className="stat-hint">View all & pin favorites</div>
          </div>
        )}
      </div>
    </section>
  );
};

export default DashboardStats;
